import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Mail, Clock } from "lucide-react";
import SEOHead from "../components/SEOHead";

/**
 * Email-change confirmation landing page.
 *
 * URL: /auth/confirm-email-change?token=<single-side token>
 *
 * Each token confirms ONE side (current-email or new-email). The endpoint
 * tracks completion via a shared `pair_id`. When both sides are confirmed
 * within the 30-min window, the email switches automatically.
 *
 * States:
 *   loading           — burning the token
 *   pending_other     — this side OK, ask user to click the partner link
 *   completed         — both sides OK, email switched, redirect to /login
 *   invalid           — token expired / already used / malformed
 */
export default function ConfirmEmailChange() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [state, setState] = useState("loading");
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post("/auth/confirm-email-change", { token })
      .then(({ data }) => {
        setResult(data);
        setState(data.status === "completed" ? "completed" : "pending_other");
        if (data.status === "completed") {
          // Force a fresh login because session_version was bumped.
          localStorage.removeItem("sdf_token");
        }
      })
      .catch((err) => {
        setErrMsg(formatApiError(err));
        setState("invalid");
      });
  }, [token]);

  return (
    <div className="container-x py-24 max-w-md text-center" data-testid="confirm-email-change-page">
      <SEOHead title="Confirm email change | Sri Durga Foods" description="Confirming your new email address." noindex />
      {state === "loading" && (
        <>
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-brand-chili mb-4" />
          <p className="text-brand-muted">Confirming your email change…</p>
        </>
      )}

      {state === "pending_other" && (
        <>
          <Clock className="w-14 h-14 text-amber-600 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-brand-dark mb-2" data-testid="confirm-pending-title">
            One more step to go
          </h1>
          <p className="text-sm text-brand-muted mb-4">
            We&apos;ve confirmed this side of the change.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 text-left mb-6">
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong className="block mb-1">{result?.message}</strong>
                <span className="text-xs">New email: <code className="font-mono">{result?.new_email}</code></span>
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/login")} className="btn-secondary" data-testid="confirm-pending-back">
            Back to sign in
          </button>
        </>
      )}

      {state === "completed" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-brand-dark mb-2" data-testid="confirm-completed-title">
            Email address updated successfully
          </h1>
          <p className="text-sm text-brand-muted mb-6">
            Your admin email is now <strong className="text-brand-dark">{result?.new_email}</strong>. All sessions have been signed out — please sign in again with the new email.
          </p>
          <button onClick={() => navigate("/login")} className="btn-primary" data-testid="confirm-completed-signin">
            Go to sign in
          </button>
        </>
      )}

      {state === "invalid" && (
        <>
          <XCircle className="w-14 h-14 text-brand-chili mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-brand-dark mb-2" data-testid="confirm-invalid-title">
            Verification link is invalid or expired
          </h1>
          <p className="text-sm text-brand-muted mb-2">
            {errMsg || "This link can only be used once and expires after 30 minutes."}
          </p>
          <p className="text-sm text-brand-muted mb-6">
            Sign in and request a fresh email change from <em>Admin → Security → Change email</em>.
          </p>
          <button onClick={() => navigate("/login")} className="btn-primary" data-testid="confirm-invalid-back">
            Back to sign in
          </button>
        </>
      )}
    </div>
  );
}
